import { login } from "@/components/access/ducks/Actions"
import AppContainer from "@/components/AppContainer"
import { setCustomer } from "@/components/customer/ducks/Actions"
import getCustomerStripeKey from "@/components/donations/helpers/getCustomerStripeKey"
import {
  backgroundWhite,
  borderNone,
  colorSecondaryTextColor,
  displayNone,
  drawerPaper,
  flexAuto,
} from "@/components/Styles"
import QueryProvider from "@/query/provider"
import * as colors from "@mui/material/colors"
import {
  alpha,
  // adaptV4Theme,
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material/styles"
import { init } from "@sentry/browser"
import PropTypes from "prop-types"
import { Provider } from "react-redux"
import { unstable_HistoryRouter as HistoryRouter } from "react-router-dom"
import history from "routing/history"

const propTypes = {
  store: PropTypes.shape().isRequired,
  bootstrapData: PropTypes.shape({
    customer: PropTypes.object,
    sentryTracking: PropTypes.bool,
    csrfToken: PropTypes.string,
    message: PropTypes.string,
    seoData: PropTypes.shape({
      customerName: PropTypes.string,
      customerImgUrl: PropTypes.string,
      sentryTracking: PropTypes.bool,
      pingdomTracking: PropTypes.bool,
    }),
  }).isRequired,
}

const tmpTheme = createTheme({
  palette: {
    grey: {
      main: colors.grey[300],
      dark: colors.grey[400],
    },
  },
})

/**
 * @param {{store: AppStore, bootstrapData: object}} props
 */
function Wavelength(props) {
  const { store, bootstrapData } = props

  // Use bootstrap data from props instead of window globals
  if (bootstrapData?.customer) {
    const customer = bootstrapData.customer

    if (!customer.hideInternalDonations) {
      customer.stripeKey = getCustomerStripeKey(customer)
    }

    store.dispatch(setCustomer(customer))
    if (customer.loggedIn) {
      store.dispatch(login())
    }
  }

  // Initialize Sentry if enabled in bootstrap data
  if (bootstrapData?.sentryTracking) {
    init({
      dsn: "https://54e1f0f218d94e0b92e28672df956673@sentry.io/1244953",
    })
  }

  // Store message in window for components that might still expect it
  if (bootstrapData?.message) {
    window.message = bootstrapData.message
  }

  // useEffect(() => {
  //   const ac = new AbortController()
  //
  //   console.debug("sending req")
  //   httpGet(
  //     "api/customer-view",
  //     {},
  //     {
  //       /** @param {{redirect: boolean, custView: Rec}} d */
  //       success: (d) => {
  //         console.debug("in success", d)
  //       },
  //       error: (err) => console.error(err),
  //       // signal: ac.signal,
  //     },
  //   )
  //   // .then(
  //   //   /** @param {{redirect: boolean, custView: Rec}} data */
  //   //   (data) => {
  //   //     console.debug(data)
  //   //     if (data.redirect) {
  //   //       console.debug("shoudl redirect")
  //   //     } else {
  //   //       store.dispatch(setCustomer(data.custView))
  //   //       if (data.custView.loggedIn) {
  //   //         store.dispatch(login())
  //   //       }
  //   //     }
  //   //   },
  //   // )
  //   // .catch((err) => console.error(err))
  //
  //   return () => {
  //     ac.abort()
  //   }
  // }, [])
  //
  // if (!customer) {
  //   return <CircularProgress />
  // }

  const _checkOnline = () => {
    if (Offline.state === "up") {
      Offline.check()
    }
  }

  // Choose the stripe key
  // if (customer && !customer.hideInternalDonations) {
  //   customer.stripeKey = getCustomerStripeKey(customer)
  // }

  const customer = bootstrapData.customer
  const custColor = colors[customer?.colors?.primary] || colors.blue
  // const custColor = colors.teal // TODO: doesn't match with ivy
  const primaryColor = tmpTheme.palette.augmentColor({
    color: {
      ...custColor,
      main: custColor[500],
    },
    name: "primary",
  })
  const secondaryColor = tmpTheme.palette.augmentColor({
    color: {
      ...custColor,
      main: custColor[50],
    },
    name: "secondary",
  })
  const isMobile = false

  let newTheme = createTheme(tmpTheme, {
    palette: {
      mode: "light",
      primary: primaryColor,
      secondary: secondaryColor,
    },
  })

  newTheme = createTheme(newTheme, {
    components: {
      MuiCheckbox: { defaultProps: { color: "primary" } },
      MuiChip: { defaultProps: { size: "small", variant: "outlined" } },
      MuiDialog: { defaultProps: { fullWidth: true, fullScreen: isMobile } },
      MuiDivider: { defaultProps: { light: true } },
      MuiFab: { defaultProps: { color: "primary", size: "small" } },
      MuiFormControl: { defaultProps: { variant: "outlined" } },
      MuiGrid: { defaultProps: { spacing: 2 } },
      MuiPaper: { defaultProps: { variant: "outlined" } },
      MuiSelect: { defaultProps: { variant: "outlined" } },
      MuiSwitch: { defaultProps: { color: "primary" } },
      MuiTextField: { defaultProps: { variant: "outlined", margin: "normal" } },

      // Style overrides
      MuiTypography: { styleOverrides: { caption: colorSecondaryTextColor } },
      MuiOutlinedInput: { styleOverrides: { root: backgroundWhite } },
      MUIDataTableSearch: {
        styleOverrides: {
          searchIcon: displayNone,
          searchText: flexAuto,
          clearIcon: displayNone,
        },
      },
      MUIDataTable: { styleOverrides: { paper: borderNone } },
      MUIDataTablePagination: {
        styleOverrides: { root: { "&:last-child": borderNone } },
      },
      MUIDataTableToolbarSelect: { styleOverrides: { title: displayNone } },

      // Both
      MuiDrawer: {
        defaultProps: { anchor: "right" },
        styleOverrides: { paper: drawerPaper },
      },

      // With variants
      MuiButton: {
        defaultProps: { disableElevation: true },
        // https://mui.com/material-ui/migration/v5-component-changes/#button
        // https://codesandbox.io/p/sandbox/mimic-v4-button-default-color-bklx8?file=/src/Demo.tsx
        variants: [
          {
            props: { variant: "text", color: "grey" },
            style: {
              color: newTheme.palette.text.primary,
              "&:hover": {
                backgroundColor: alpha(
                  newTheme.palette.text.primary,
                  newTheme.palette.action.hoverOpacity,
                ),
              },
            },
          },
          {
            props: { color: "grey", variant: "outlined" },
            style: {
              color: tmpTheme.palette.text.primary,
              "&:hover": {
                backgroundColor: alpha(
                  newTheme.palette.text.primary,
                  newTheme.palette.action.hoverOpacity,
                ),
              },
              border: "1px solid rgba(0, 0, 0, 0.23)", // TODO: make dynamic
            },
          },
          {
            props: { variant: "contained", color: "secondary" },
            style: {
              color: newTheme.palette.getContrastText(secondaryColor.main),
              "&:hover": {
                backgroundColor: alpha(
                  newTheme.palette.text.primary,
                  // tmpTheme.palette.getContrastText(secondaryColor), // TODO: make dynamic
                  0.3,
                ),
              },
            },
          },
        ],
      },
    },
  })

  return (
    <Provider store={store}>
      <HistoryRouter history={history}>
        <QueryProvider>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={newTheme}>
              <AppContainer theme={newTheme} />
            </ThemeProvider>
          </StyledEngineProvider>
        </QueryProvider>
      </HistoryRouter>
    </Provider>
  )
}

Wavelength.propTypes = propTypes

export default Wavelength
